import React, { useState, useEffect, useCallback } from 'react';
import { FaMobileAlt, FaTabletAlt, FaDesktop, FaFileImport } from 'react-icons/fa';
import { EmployeeOverview } from '../services/EmployeeService';
import TimeTrackingService, { TimeTracking } from '../../timetracking/services/TimeTrackingService';
import DateRangeFilter, { DateRange } from '../../timetracking/components/DateRangeFilter';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';
import dateUtil from '../../../utils/dateUtil';

interface EmployeeTimeTrackingTabProps {
  employeeId: number;
  employee: EmployeeOverview;
}

const EmployeeTimeTrackingTab: React.FC<EmployeeTimeTrackingTabProps> = ({ employeeId, employee }) => {
  const [timeTrackings, setTimeTrackings] = useState<TimeTracking[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  const isDeactivated = employee.onboarding_status === 'DEACTIVATED';
  const deactivatedAt = (employee as any).deactivated_at;

  const initialRange = React.useMemo(() => {
    if (isDeactivated && deactivatedAt) {
      const refDate = new Date(deactivatedAt);
      const lookbackStart = new Date(refDate);
      lookbackStart.setDate(lookbackStart.getDate() - 30);
      return {
        start: lookbackStart.toISOString().split('T')[0],
        end: refDate.toISOString().split('T')[0],
      };
    }
    return undefined;
  }, [isDeactivated, deactivatedAt]);

  const loadTimeTrackings = useCallback(async (range: DateRange) => {
    setLoading(true);
    try {
      const startStr = range.start.toISOString().split('T')[0];
      const endStr = range.end.toISOString().split('T')[0];
      const tts = await TimeTrackingService.fetchTimeTrackings({
        employee: employeeId,
        start_date: startStr,
        end_date: endStr,
      });
      const sorted = tts.sort((a, b) =>
        new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
      );
      setTimeTrackings(sorted);
    } catch (error) {
      console.error('[EmployeeTimeTrackingTab] Failed to load time trackings:', error);
      setTimeTrackings([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  const handleRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    loadTimeTrackings(range);
  }, [loadTimeTrackings]);

  useEffect(() => {
    if (!dateRange && !initialRange) {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      loadTimeTrackings({ start, end });
    }
  }, [dateRange, initialRange, loadTimeTrackings]);

  const normalizeUTC = (iso?: string | null): string => {
    if (!iso) return '';
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/.test(iso)) return `${iso}Z`;
    return iso;
  };

  const toUTCIfNaiveLocal = (iso?: string | null): string | undefined => {
    if (!iso) return undefined;
    if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)) return iso;
    const dStr = dateUtil.extractDateFromISO(iso);
    const tStr = dateUtil.extractTimeFromISO(iso);
    const utc = dStr && tStr ? dateUtil.createTenantTimeAsUTC(dStr, tStr) : null;
    return utc || iso;
  };

  const normISO = (iso: string): string => toUTCIfNaiveLocal(iso) || normalizeUTC(iso);

  const formatDateWithWeekday = (iso: string): string => {
    if (!iso) return '';
    const dt = dateUtil.convertUTCToTenantTime(normISO(iso));
    if (!dt) return '';
    const weekday = dt.toLocaleDateString('de-DE', { weekday: 'short' });
    const dateStr = dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
    return `${weekday}, ${dateStr}`;
  };

  const formatTime = (iso: string): string => {
    if (!iso) return '';
    const dt = dateUtil.convertUTCToTenantTime(normISO(iso));
    if (!dt) return '';
    return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
  };

  const calcNetMs = (tt: TimeTracking): number => {
    if (!tt.start_time || !tt.end_time) return 0;
    const st = new Date(normISO(tt.start_time));
    const et = new Date(normISO(tt.end_time));
    let ms = et.getTime() - st.getTime();
    if (tt.breakperiods && tt.breakperiods.length > 0) {
      tt.breakperiods.forEach(bp => {
        if (bp.start_time && bp.end_time) {
          const bs = new Date(bp.start_time);
          const be = new Date(bp.end_time);
          if (be > bs) ms -= (be.getTime() - bs.getTime());
        }
      });
    } else if (tt.break_start && tt.break_end) {
      const bs = new Date(tt.break_start);
      const be = new Date(tt.break_end);
      if (be > bs) ms -= (be.getTime() - bs.getTime());
    }
    return Math.max(0, ms);
  };

  const calcBreakMinutes = (tt: TimeTracking): number => {
    if (tt.breakperiods && tt.breakperiods.length > 0) {
      let total = 0;
      tt.breakperiods.forEach(bp => {
        if (bp.start_time && bp.end_time) {
          const bs = new Date(bp.start_time);
          const be = new Date(bp.end_time);
          if (be > bs) total += Math.round((be.getTime() - bs.getTime()) / 60000);
        }
      });
      return total;
    }
    if (tt.break_start && tt.break_end) {
      const bs = new Date(tt.break_start);
      const be = new Date(tt.break_end);
      if (be > bs) return Math.round((be.getTime() - bs.getTime()) / 60000);
    }
    return 0;
  };

  const renderSourceIcon = (tt: TimeTracking) => {
    const srcRaw = String((tt as any).created_via || (tt as any).source_type || (tt as any).origin || '').toLowerCase();
    let Icon: any = null;
    let title = '';
    if (srcRaw === 'import_file') { Icon = FaFileImport; title = 'Import'; }
    else if (srcRaw === 'employee_app') { Icon = FaMobileAlt; title = 'Employee App'; }
    else if (srcRaw === 'station_app') { Icon = FaTabletAlt; title = 'Station App'; }
    else if (srcRaw === 'admin_desk') { Icon = FaDesktop; title = 'Admin'; }
    if (!Icon) return <span style={{ color: colors.textLight }}>—</span>;
    return (
      <span title={title} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: colors.navy, transform: srcRaw === 'station_app' ? 'rotate(90deg)' : undefined }}>
        <Icon size={14} />
      </span>
    );
  };

  const totalHours = timeTrackings.reduce((sum, tt) => sum + calcNetMs(tt) / 3600000, 0);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <DateRangeFilter
          onRangeChange={handleRangeChange}
          initialRange={initialRange}
        />
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <span style={{ fontSize: 13, color: colors.textMuted }}>
          {timeTrackings.length} entries
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, color: colors.navy }}>
          Total: {totalHours.toFixed(1)}h
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: colors.textMuted, fontSize: 14 }}>
          Loading...
        </div>
      ) : timeTrackings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: colors.textLight, fontSize: 14, fontStyle: 'italic' }}>
          No time trackings in this period
        </div>
      ) : (
        <div style={{
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          overflow: 'hidden',
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
          }}>
            <thead>
              <tr style={{ background: colors.bgMuted }}>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'center', width: 44 }}>Source</th>
                <th style={thStyle}>Start</th>
                <th style={thStyle}>End</th>
                <th style={thStyle}>Break</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Duration</th>
                <th style={thStyle}>Work Area</th>
              </tr>
            </thead>
            <tbody>
              {timeTrackings.map((tt) => {
                const netMs = calcNetMs(tt);
                const hours = netMs / 3600000;
                const breakMins = calcBreakMinutes(tt);

                return (
                  <tr key={tt.id} style={{ borderTop: `1px solid ${colors.borderLight}` }}>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 500, color: colors.textBody }}>
                        {formatDateWithWeekday(tt.start_time)}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {renderSourceIcon(tt)}
                    </td>
                    <td style={tdStyle}>{formatTime(tt.start_time)}</td>
                    <td style={tdStyle}>{formatTime(tt.end_time)}</td>
                    <td style={tdStyle}>
                      {breakMins > 0 ? `${breakMins} min` : '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: colors.navy }}>
                      {hours.toFixed(1)}h
                    </td>
                    <td style={tdStyle}>
                      {tt.work_area__name || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  color: '#64748b',
  fontSize: 13,
};

export default EmployeeTimeTrackingTab;
